// src/stores/date.js
import { defineStore } from "pinia";
import { supabase } from "src/boot/supabase";

const appVersion = process.env.APP_VERSION || "0.0.0";

export const useSettingsStore = defineStore("settings", {
  state: () => ({
    pageAnimationState: "",
    leftState: "animated fadeInLeft",
    rightState: "animated fadeInRight",
    upState: "animated fadeInUp",
    downState: "animated fadeInDown",
    localeState: localStorage.getItem("locale")
      ? localStorage.getItem("locale")
      : "nl-NL", //default to nl-NL
    userNameState: localStorage.getItem("userName")
      ? localStorage.getItem("userName")
      : "",
    userDateState: localStorage.getItem("userDate")
      ? localStorage.getItem("userDate")
      : "",
    nameState: localStorage.getItem("name") ? localStorage.getItem("name") : "",
    dateState: localStorage.getItem("date") ? localStorage.getItem("date") : "",
    name2State: localStorage.getItem("name2")
      ? localStorage.getItem("name2")
      : "",
    date2State: localStorage.getItem("date2")
      ? localStorage.getItem("date2")
      : "",
    usersState: [],
    userListState: localStorage.getItem("userlist")
      ? JSON.parse(localStorage.getItem("userlist"))
      : [],
    sessionState: null,
    pricesState: null,
    productsState: [],
    testimonialsState: [],
    showSurveyButtonState: false,
    isAdminState: false,
    printImagesState: localStorage.getItem("printImages")
      ? JSON.parse(localStorage.getItem("printImages"))
      : [],
    // Wheel Config
    wheelConfigState: localStorage.getItem("wheelConfig")
      ? JSON.parse(localStorage.getItem("wheelConfig"))
      : {
          x: 0,
          y: 0,
          scale: 1,
          rotation: 0,
          opacity: 1,
          zIndex: 10,
          blendMode: "normal",
        },
    // Title Config
    titleConfigState: localStorage.getItem("titleConfig")
      ? JSON.parse(localStorage.getItem("titleConfig"))
      : { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1, zIndex: 20 },
    // Code Config
    codeConfigState: localStorage.getItem("codeConfig")
      ? JSON.parse(localStorage.getItem("codeConfig"))
      : { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1, zIndex: 20 },
    // Explanation Config
    explanationConfigState: localStorage.getItem("explanationConfig")
      ? JSON.parse(localStorage.getItem("explanationConfig"))
      : { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1, zIndex: 20 },
    // Background Config
    backgroundConfigState: localStorage.getItem("backgroundConfig")
      ? JSON.parse(localStorage.getItem("backgroundConfig"))
      : { opacity: 1, hueRotate: 0, brightness: 100, contrast: 100 },
    // Print Settings (Toggles)
    printSettingsState: localStorage.getItem("printSettings")
      ? JSON.parse(localStorage.getItem("printSettings"))
      : {
          showWinds: false,
          printFriendly: false,
          showExplanation: false,
          showExplanationText: true,
          showTitle: true,
          showCode: true,
          withBackground: true,
        },
    // Deprecated but kept for migration if needed, though we'll likely ignore it for the new feature
    spiritAnimalConfigState: localStorage.getItem("spiritAnimalConfig")
      ? JSON.parse(localStorage.getItem("spiritAnimalConfig"))
      : { x: 0, y: 0, scale: 1, rotation: 0, flip: false, opacity: 0.8 },
  }),
  getters: {
    pageAnimation: (state) => state.pageAnimationState,
    locale: (state) => state.localeState,
    userName: (state) => state.nameState,
    userDate: (state) => state.dateState,
    name: (state) => state.nameState,
    date: (state) => state.dateState,
    name2: (state) => state.name2State,
    date2: (state) => state.date2State,
    users: (state) => state.usersState,
    session: (state) => state.sessionState,
    prices: (state) => state.pricesState,
    products: (state) => state.productsState,
    showSurveyButton: (state) => state.showSurveyButtonState,
    userList: (state) => state.userListState,
    isAdmin: (state) => state.isAdminState || false,
    isAuthenticated: (state) => state.sessionState !== null,
    testimonials: (state) => state.testimonialsState,
    spiritAnimalConfig: (state) => state.spiritAnimalConfigState,
    printImages: (state) => state.printImagesState,
    wheelConfig: (state) => state.wheelConfigState,
    titleConfig: (state) => state.titleConfigState,
    codeConfig: (state) => state.codeConfigState,
    explanationConfig: (state) => state.explanationConfigState,
    backgroundConfig: (state) => state.backgroundConfigState,
    printSettings: (state) => state.printSettingsState,

    appVersion: () => appVersion,
  },
  actions: {
    setSession(session) {
      this.sessionState = session;
    },
    setPageAnimation(string) {
      this.pageAnimationState = string;
    },
    setDirectionLeft() {
      this.pageAnimationState = this.leftState;
    },
    setDirectionRight() {
      this.pageAnimationState = this.rightState;
    },
    setDirectionDown() {
      this.pageAnimationState = this.downState;
    },
    setDirectionUp() {
      this.pageAnimationState = this.upState;
    },
    setLocale(value) {
      console.log("locale changed to ", value);
      this.localeState = value; // nl-NL || en-US
      localStorage.setItem("locale", value); // save to localStorage
    },
    saveUserName(value) {
      this.userNameState = value;
      localStorage.setItem("userName", value);
    },
    saveUserDate(value) {
      this.userDateState = value;
      localStorage.setItem("userDate", value);
    },
    saveToUserlist(object) {
      // add to localstorage user list
      // max 10 users
      // check if user already exists
      let exists = false;
      this.userListState.forEach((element) => {
        if (element.name === object.name && element.date === object.date) {
          exists = true;
        }
      });
      if (exists) {
        console.log("user already exists");
      } else {
        this.userListState.unshift(object); // add to top of list
        //remove last item if more than 10
        if (this.userListState.length > 10) {
          this.userListState.pop();
        }
        localStorage.setItem("userlist", JSON.stringify(this.userListState));
        console.log("userlist saved to localstorage", this.userListState);
      }
    },
    updateUserlist(object) {
      // update userlist
      this.userListState = object;
      localStorage.setItem("userlist", JSON.stringify(this.userListState));
      console.log("userlist updated", this.userListState);
    },
    saveName(value) {
      this.nameState = value;
      localStorage.setItem("name", value);
    },
    saveDate(value) {
      this.dateState = value;
      localStorage.setItem("date", value);
    },
    saveName2(value) {
      this.name2State = value;
      localStorage.setItem("name2", value);
    },
    saveDate2(value) {
      this.date2State = value;
      localStorage.setItem("date2", value);
    },
    saveSpiritAnimalConfig(config) {
      this.spiritAnimalConfigState = config;
      localStorage.setItem("spiritAnimalConfig", JSON.stringify(config));
    },
    // Print Images Actions
    addPrintImage(image) {
      this.printImagesState.push(image);
      this.savePrintImages();
    },
    removePrintImage(id) {
      this.printImagesState = this.printImagesState.filter(
        (img) => img.id !== id
      );
      this.savePrintImages();
    },
    updatePrintImage(updatedImage) {
      const index = this.printImagesState.findIndex(
        (img) => img.id === updatedImage.id
      );
      if (index !== -1) {
        this.printImagesState[index] = updatedImage;
        this.savePrintImages();
      }
    },
    savePrintImages() {
      try {
        localStorage.setItem(
          "printImages",
          JSON.stringify(this.printImagesState)
        );
      } catch (e) {
        console.warn(
          "Failed to save print images to localStorage (Quota Exceeded). Images will be lost on refresh.",
          e
        );
      }
    },
    // Wheel Config Actions
    saveWheelConfig(config) {
      this.wheelConfigState = config;
      localStorage.setItem("wheelConfig", JSON.stringify(config));
    },
    // Title Config Actions
    saveTitleConfig(config) {
      this.titleConfigState = config;
      localStorage.setItem("titleConfig", JSON.stringify(config));
    },
    // Code Config Actions
    saveCodeConfig(config) {
      this.codeConfigState = config;
      localStorage.setItem("codeConfig", JSON.stringify(config));
    },
    // Explanation Config Actions
    saveExplanationConfig(config) {
      this.explanationConfigState = config;
      localStorage.setItem("explanationConfig", JSON.stringify(config));
    },
    // Background Config Actions
    saveBackgroundConfig(config) {
      this.backgroundConfigState = config;
      localStorage.setItem("backgroundConfig", JSON.stringify(config));
    },
    savePrintSettings(settings) {
      this.printSettingsState = settings;
      localStorage.setItem("printSettings", JSON.stringify(settings));
    },
    setShowSurveyButton(value) {
      this.showSurveyButtonState = value;
    },

    async loadPrices() {
      const { data, error } = await supabase
        .from("prices")
        .select(
          "mini_reading, live_reading, intro_reading, report_digital, report_paperback"
        )
        .eq("id", 1)
        .single();
      if (!error && data) {
        this.pricesState = {
          mini_reading: data.mini_reading,
          live_reading: data.live_reading,
          intro_reading: data.intro_reading,
          report_digital: data.report_digital,
          report_paperback: data.report_paperback,
        };
      } else {
        console.error("loadPrices", error);
        // fallback prices
        this.pricesState = {
          mini_reading: 5,
          live_reading: 35,
          intro_reading: 25,
          report_digital: 55,
          report_paperback: 95,
        };
      }
    },

    async updatePrices(prices) {
      const updates = { id: 1, ...prices };
      const { error } = await supabase.from("prices").upsert(updates);
      if (error) {
        console.error("updatePrices", error);
        throw error;
      }
      this.pricesState = { ...prices };
      return this.pricesState;
    },
    async loadProducts() {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("price_cents", { ascending: true });

      if (error) {
        console.error("loadProducts", error);
        this.productsState = [];
        return [];
      }

      this.productsState = (data || []).sort(
        (left, right) =>
          Number(left.sort_order ?? left.price_cents ?? 100) -
            Number(right.sort_order ?? right.price_cents ?? 100) ||
          String(left.name || "").localeCompare(String(right.name || ""), "nl")
      );
      return this.productsState;
    },
    async loadTestimonials() {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .eq("active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("loadTestimonials", error);
        this.testimonialsState = [];
        return [];
      }

      this.testimonialsState = data || [];
      return this.testimonialsState;
    },
    async updateTestimonial(testimonial) {
      const updates = {
        id: testimonial.id,
        text: testimonial.text,
        author: testimonial.author,
        active: testimonial.active === true,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("testimonials")
        .upsert(updates)
        .select()
        .single();

      if (error) {
        console.error("updateTestimonial", error);
        throw error;
      }

      const index = this.testimonialsState.findIndex(
        (item) => item.id === data.id
      );
      if (index >= 0) {
        this.testimonialsState.splice(index, 1, data);
      } else {
        this.testimonialsState.push(data);
      }
      return data;
    },
    async updateProduct(product) {
      const updates = {
        slug: product.slug,
        name: product.name,
        type: product.type,
        description: product.description,
        price_cents: Number(product.price_cents || 0),
        shipping_price_cents: Math.max(
          0,
          Number(product.shipping_price_cents || 0)
        ),
        currency: product.currency || "eur",
        active: product.active === true,
        shop_status: product.shop_status || "active",
        sort_order: Math.max(0, Number(product.sort_order ?? 100)),
        stripe_product_id: product.stripe_product_id || null,
        stripe_price_id: product.stripe_price_id || null,
        metadata: product.metadata || {},
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("products")
        .upsert(updates)
        .select()
        .single();

      if (error) {
        console.error("updateProduct", error);
        throw error;
      }

      const index = this.productsState.findIndex(
        (item) => item.slug === data.slug
      );
      if (index >= 0) {
        this.productsState.splice(index, 1, data);
      } else {
        this.productsState.push(data);
      }
      this.productsState.sort(
        (left, right) =>
          Number(left.sort_order ?? 100) - Number(right.sort_order ?? 100) ||
          String(left.name || "").localeCompare(String(right.name || ""), "nl")
      );
      return data;
    },
    async checkAdminStatus() {
      console.log("Checking admin status...");
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("Error fetching session:", error);
        this.isAdminState = false;
        return;
      }
      //console.log("Session:", data);
      const session = data.session;
      if (!session) {
        console.error("No active session found");
        this.isAdminState = false;
        return;
      }
      console.log("Session found");

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", session.user.id)
        .single();

      if (profileError) {
        console.error("Error checking admin status:", profileError);
        this.isAdminState = false;
      } else {
        console.log("Admin status data:", profileData);
        this.isAdminState = profileData?.role === "admin";
      }
    },
  },
});
